import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { LookupFieldSelect } from '../../components/formFields/LookupFieldSelect';
import { getFileCategories, getSubFileCategories, type LookupItem } from '../../services/criminalCaseRequests';
import {
  listLetterTemplates,
  listLetterTemplatesBySubCategory,
  type LetterTemplateListRow,
  type LetterTemplateNameOption,
} from '../../services/letterTemplateMasters';
import { MD_PERMISSIONS as MD } from '../../rbac/metadataPermissions';
import { PermissionGate } from '../../rbac/guards';
import { consumeFlashToast } from '../../utils/flashToast';
import { formatDocumentTableDateTime } from '../../utils/dateFormat';
import { useAuth } from '../../contexts/AuthContext';

const EMPTY = '\u2014';
type StatusFilter = '' | 'active' | 'inactive';

export default function LetterTemplatesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<LetterTemplateListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);

  const [draftCategoryId, setDraftCategoryId] = useState('');
  const [draftSubCategoryId, setDraftSubCategoryId] = useState('');
  const [draftTemplateName, setDraftTemplateName] = useState('');
  const [draftStatus, setDraftStatus] = useState<StatusFilter>('');

  const [categoryId, setCategoryId] = useState('');
  const [subCategoryId, setSubCategoryId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');

  const [uiPage, setUiPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [fileCategories, setFileCategories] = useState<LookupItem[]>([]);
  const [draftSubCategories, setDraftSubCategories] = useState<LookupItem[]>([]);
  const [templateNameOptions, setTemplateNameOptions] = useState<LetterTemplateNameOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [subOptionsLoading, setSubOptionsLoading] = useState(false);
  const [templateOptionsLoading, setTemplateOptionsLoading] = useState(false);

  useEffect(() => {
    const flash = consumeFlashToast();
    if (flash?.message) {
      setToast(flash.message);
      window.setTimeout(() => setToast(null), 3200);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setOptionsLoading(true);
    void getFileCategories().then((catsRes) => {
      if (!alive) return;
      setFileCategories(catsRes.success ? catsRes.data : []);
      setOptionsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!draftCategoryId) {
      setDraftSubCategories([]);
      return;
    }
    let alive = true;
    setSubOptionsLoading(true);
    void getSubFileCategories(draftCategoryId, { useCache: false }).then((res) => {
      if (!alive) return;
      setDraftSubCategories(res.success ? res.data : []);
      setSubOptionsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [draftCategoryId]);

  useEffect(() => {
    if (!draftSubCategoryId) {
      setTemplateNameOptions([]);
      setDraftTemplateName('');
      setTemplateOptionsLoading(false);
      return;
    }

    const controller = new AbortController();
    setTemplateOptionsLoading(true);
    void listLetterTemplatesBySubCategory(draftSubCategoryId, controller.signal).then((res) => {
      if (controller.signal.aborted) return;
      setTemplateNameOptions(res.success ? res.data : []);
      setTemplateOptionsLoading(false);
      if (!res.success) return;
      setDraftTemplateName((current) => {
        if (!current) return current;
        return res.data.some((opt) => opt.name === current) ? current : '';
      });
    });

    return () => {
      controller.abort();
      setTemplateOptionsLoading(false);
    };
  }, [draftSubCategoryId]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listLetterTemplates({
        page: Math.max(0, uiPage - 1),
        pageSize,
        categoryId: categoryId || undefined,
        subCategoryId: subCategoryId || undefined,
        templateName: templateName || undefined,
        status: status === '' ? undefined : status === 'active',
        sortBy: 'last_updated_at',
        sortDir: 'DESC',
      });
      if (!res.success) throw new Error(res.message);
      setRows(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
      if (uiPage > res.data.totalPages) {
        setUiPage(res.data.totalPages);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('metadata.letterTemplates.loadFailed'));
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [categoryId, pageSize, reloadToken, status, subCategoryId, templateName, t, uiPage]);

  useEffect(() => {
    if (!user) return;
    void loadList();
  }, [user?.id, loadList]);

  const columns = useMemo<Column<LetterTemplateListRow>[]>(() => {
    return [
      {
        key: 'categoryName',
        header: t('admin.shared.fileCategory'),
        getValue: (r) => r.categoryName,
        render: (r) => <span style={{ fontWeight: 600 }}>{r.categoryName || EMPTY}</span>,
      },
      {
        key: 'subCategoryName',
        header: t('admin.shared.subFileCategory'),
        getValue: (r) => r.subCategoryName,
        render: (r) => r.subCategoryName || EMPTY,
      },
      {
        key: 'templateName',
        header: t('metadata.letterTemplates.templateName'),
        getValue: (r) => r.templateName,
        render: (r) => r.templateName || EMPTY,
      },
      {
        key: 'active',
        header: t('common.status'),
        getValue: (r) => (r.active ? t('common.active') : t('common.inactive')),
        render: (r) => (
          <span className={`al-pill al-pill-${r.active ? 'file-d' : 'user-d'}`}>
            {r.active ? t('common.active') : t('common.inactive')}
          </span>
        ),
      },
      {
        key: 'updatedAt',
        header: t('metadata.letterTemplates.lastUpdatedAt'),
        getValue: (r) => r.updatedAt,
        render: (r) => formatDocumentTableDateTime(r.updatedAt) || EMPTY,
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
              <Link
                className="act-b"
                to={`/metadata/letter-templates/${encodeURIComponent(r.id)}/edit`}
                data-tip={t('metadata.letterTemplates.editTemplateName')}
                aria-label={t('metadata.letterTemplates.editTemplateName')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            </PermissionGate>
          </div>
        ),
      },
    ];
  }, [t]);

  const resetFilters = () => {
    setDraftCategoryId('');
    setDraftSubCategoryId('');
    setDraftTemplateName('');
    setTemplateNameOptions([]);
    setDraftStatus('');
    setCategoryId('');
    setSubCategoryId('');
    setTemplateName('');
    setStatus('');
    setUiPage(1);
    setPageSize(10);
    setReloadToken((n) => n + 1);
  };

  const applyFilters = () => {
    setCategoryId(draftCategoryId);
    setSubCategoryId(draftSubCategoryId);
    setTemplateName(draftTemplateName);
    setStatus(draftStatus);
    setUiPage(1);
    setReloadToken((n) => n + 1);
  };

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        {t('metadata.letterTemplates.title')}
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('metadata.letterTemplates.title')}</div>
        </div>
      </div>

      {toast ? <div className="si-ok" style={{ marginBottom: 12 }}>{toast}</div> : null}

      <div className="flt-card">
        <div className="flt-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          <LookupFieldSelect
            label={t('admin.shared.fileCategory')}
            value={draftCategoryId}
            placeholder={optionsLoading ? t('common.loading') : t('metadata.letterTemplates.selectFileCategory')}
            options={fileCategories.map((item) => ({ id: item.id, label: item.name }))}
            disabled={optionsLoading}
            searchable
            emptyMessage={t('common.noDataFound')}
            onChange={(value) => {
              setDraftCategoryId(value);
              setDraftSubCategoryId('');
              setDraftTemplateName('');
            }}
          />
          <LookupFieldSelect
            label={t('admin.shared.subFileCategory')}
            value={draftSubCategoryId}
            placeholder={
              !draftCategoryId
                ? t('metadata.letterTemplates.selectFileCategoryFirst')
                : subOptionsLoading
                  ? t('common.loading')
                  : t('metadata.letterTemplates.selectSubFileCategory')
            }
            options={draftSubCategories.map((item) => ({ id: item.id, label: item.name }))}
            disabled={!draftCategoryId || subOptionsLoading}
            searchable
            emptyMessage={t('common.noDataFound')}
            onChange={(value) => {
              setDraftSubCategoryId(value);
              setDraftTemplateName('');
            }}
          />
          <LookupFieldSelect
            label={t('metadata.letterTemplates.templateName')}
            value={draftTemplateName}
            placeholder={
              !draftSubCategoryId
                ? t('metadata.letterTemplates.selectSubFileCategoryFirst')
                : templateOptionsLoading
                  ? t('common.loading')
                  : t('metadata.letterTemplates.selectTemplateName')
            }
            options={templateNameOptions.map((item) => ({ id: item.name, label: item.name }))}
            disabled={!draftSubCategoryId || templateOptionsLoading}
            searchable
            emptyMessage={t('common.noDataFound')}
            onChange={setDraftTemplateName}
          />
          <LookupFieldSelect
            label={t('common.status')}
            value={draftStatus}
            placeholder={t('metadata.letterTemplates.selectStatus')}
            options={[
              { id: 'active', label: t('common.active') },
              { id: 'inactive', label: t('common.inactive') },
            ]}
            onChange={(v) => setDraftStatus(v as StatusFilter)}
          />
        </div>
        <div className="flt-actions">
          <button className="flt-btn" type="button" onClick={resetFilters}>
            {t('common.reset')}
          </button>
          <button className="flt-btn pr" type="button" onClick={applyFilters}>
            {t('common.search')}
          </button>
        </div>
      </div>

      {error ? <div className="si-err">{error}</div> : null}

      <DataTable
        title={t('metadata.letterTemplates.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        emphasizedHeader
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[5, 10, 15, 25]}
        footerCountNoun={t('metadata.letterTemplates.footerCountNoun')}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
        serverPaging={{
          totalCount: total,
          totalPages,
          page: uiPage,
          pageSize,
          onPageChange: (p) => setUiPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setUiPage(1);
          },
        }}
      />
    </>
  );
}
